import { useState, useRef, useEffect } from 'react';
import { Sparkles, Loader2, AlertCircle, Zap, ExternalLink, ArrowUpRight, Database } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { cn } from '@/lib/utils';
import type { HAREntry } from '@har-insight/parser';
import { formatBytes, formatTime } from '@har-insight/parser';
import { OfflineIndicator } from './OfflineIndicator';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

interface AIInsight {
  type: 'critical' | 'warning' | 'suggestion' | 'info';
  category: string;
  title: string;
  description: string;
  impact?: string;
  recommendation?: string;
}

/**
 * Get documentation URL based on category
 */
function getDocumentationUrl(category: string): string | null {
  const categoryLower = category.toLowerCase();
  
  if (categoryLower.includes('performance')) {
    return 'https://web.dev/performance-scoring/';
  }
  if (categoryLower.includes('security')) {
    return 'https://web.dev/security/';
  }
  if (categoryLower.includes('cache')) {
    return 'https://web.dev/articles/http-cache';
  }
  if (categoryLower.includes('compression')) {
    return 'https://web.dev/articles/optimizing-content-efficiency-compression';
  }
  if (categoryLower.includes('image')) {
    return 'https://web.dev/articles/compress-images-avif';
  }
  if (categoryLower.includes('font')) {
    return 'https://web.dev/articles/font-best-practices';
  }
  if (categoryLower.includes('javascript') || categoryLower.includes('js')) {
    return 'https://web.dev/articles/optimize-javascript';
  }
  if (categoryLower.includes('css')) {
    return 'https://web.dev/articles/optimize-css';
  }
  if (categoryLower.includes('network')) {
    return 'https://web.dev/articles/fast';
  }
  
  return null;
}

/**
 * Find all matching requests for an AI insight
 * Returns up to 5 matches sorted by relevance
 */
function findMatchingRequests(insight: AIInsight, entries: HAREntry[]): HAREntry[] {
  if (!entries || entries.length === 0) return [];
  
  const searchText = (insight.title + ' ' + insight.description).toLowerCase();
  const matches: { entry: HAREntry; relevance: number }[] = [];
  
  // Strategy 1: Look for full URLs (highest relevance)
  const urlMatch = searchText.match(/https?:\/\/[^\s)]+/);
  if (urlMatch) {
    const url = urlMatch[0];
    for (const entry of entries) {
      if (entry.request.url.toLowerCase().includes(url)) {
        matches.push({ entry, relevance: 100 });
      }
    }
  }
  
  // Strategy 2: Look for path patterns like /api/endpoint
  if (matches.length === 0) {
    const paths = searchText.match(/(\/[a-zA-Z0-9._-]+)/g);
    if (paths) {
      for (const path of paths) {
        if (path.length > 3) {
          for (const entry of entries) {
            const entryUrl = entry.request.url.toLowerCase();
            const pathLower = path.toLowerCase();
            if (entryUrl.includes(pathLower) && !matches.some(m => m.entry === entry)) {
              matches.push({ entry, relevance: 50 });
            }
          }
        }
      }
    }
  }
  
  // Strategy 3: Look for filename patterns
  if (matches.length === 0) {
    const parts = searchText.split(/[\s,()]+/);
    for (const part of parts) {
      if (part.includes('.') && part.length > 5) {
        for (const entry of entries) {
          const entryUrl = entry.request.url.toLowerCase();
          const partLower = part.toLowerCase();
          if ((entryUrl.endsWith(partLower) || entryUrl.includes('/' + partLower)) && 
              !matches.some(m => m.entry === entry)) {
            matches.push({ entry, relevance: 25 });
          }
        }
      }
    }
  }
  
  // Sort by relevance and return top 5
  return matches
    .sort((a, b) => b.relevance - a.relevance)
    .slice(0, 5)
    .map(m => m.entry);
}

/**
 * Get status code background color class
 */
function getStatusBgColor(status: number): string {
  if (status >= 200 && status < 300) return 'bg-emerald-100 dark:bg-emerald-500/20';
  if (status >= 300 && status < 400) return 'bg-amber-100 dark:bg-amber-500/20';
  if (status >= 400) return 'bg-red-100 dark:bg-red-500/20';
  return 'bg-slate-100 dark:bg-slate-500/20';
}

/**
 * Component to display matched requests in a compact table
 */
interface MatchedRequestsTableProps {
  requests: HAREntry[];
  onRequestClick: (entry: HAREntry) => void;
}

function MatchedRequestsTable({ requests, onRequestClick }: MatchedRequestsTableProps) {
  if (!requests || requests.length === 0) return null;
  
  const displayRequests = requests.slice(0, 5);
  const hasMore = requests.length > 5;
  const remainingCount = requests.length - 5;
  
  const truncateUrl = (url: string, maxLength: number = 50): string => {
    if (url.length <= maxLength) return url;
    const start = url.substring(0, 20);
    const end = url.substring(url.length - 25);
    return `${start}...${end}`;
  };
  
  return (
    <div className="mt-3 bg-white/40 dark:bg-black/10 rounded-lg border border-white/20 dark:border-white/5 overflow-hidden">
      <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-white/20 dark:border-white/5">
        Matched Requests
      </div>
      <div className="max-h-[120px] overflow-y-auto">
        {displayRequests.map((entry, index) => (
          <button
            key={entry._id || index}
            onClick={() => onRequestClick(entry)}
            className={cn(
              "w-full text-left px-2 py-1.5 hover:bg-white/50 dark:hover:bg-white/10 transition-colors flex items-center gap-2 border-b border-white/10 dark:border-white/5 last:border-b-0",
              "group"
            )}
          >
            <span className={cn(
              "text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0",
              getStatusBgColor(entry.response.status)
            )}>
              {entry.response.status}
            </span>
            <span className="text-[9px] font-semibold text-slate-600 dark:text-slate-400 flex-shrink-0 w-10">
              {entry.request.method}
            </span>
            <span className="text-[10px] text-slate-700 dark:text-slate-300 truncate flex-1 min-w-0 font-medium" title={entry.request.url}>
              {truncateUrl(entry.request.url)}
            </span>
            <span className="text-[9px] text-slate-500 dark:text-slate-500 flex-shrink-0 tabular-nums">
              {formatTime(entry.time || 0)}
            </span>
            <span className="text-[9px] text-slate-500 dark:text-slate-500 flex-shrink-0 tabular-nums w-12 text-right">
              {formatBytes(entry.response.content.size || 0)}
            </span>
            <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 flex-shrink-0" />
          </button>
        ))}
      </div>
      {hasMore && (
        <div className="px-2 py-1 text-[10px] text-slate-500 dark:text-slate-400 italic border-t border-white/20 dark:border-white/5">
          + {remainingCount} more request{remainingCount > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

export function AIInsights() {
  const { har, aiInsights, aiInsightsLoading, aiInsightsError, aiInsightsCached, pendingAnalysesCount, loadAIInsights, retryPendingAnalyses, selectEntry, setSidebarOpen } = useHARStore();
  const { isOnline } = useNetworkStatus();
  const [hasNewInsights, setHasNewInsights] = useState(false);
  const prevInsightsLength = useRef(0);

  // Track when new insights are loaded
  useEffect(() => {
    if (aiInsights && aiInsights.length > 0 && !aiInsightsLoading) {
      if (aiInsights.length > prevInsightsLength.current) {
        setHasNewInsights(true);
        const timer = setTimeout(() => setHasNewInsights(false), 3000);
        return () => clearTimeout(timer);
      }
      prevInsightsLength.current = aiInsights.length;
    }
  }, [aiInsights, aiInsightsLoading]);

  const getInsightStyles = (type: AIInsight['type']) => {
    switch (type) {
      case 'critical':
        return 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-400';
      case 'warning':
        return 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400';
      case 'suggestion':
        return 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400';
      case 'info':
        return 'bg-slate-50 dark:bg-slate-500/10 border-slate-200 dark:border-slate-500/30 text-slate-700 dark:text-slate-400';
      default:
        return 'bg-slate-50 dark:bg-slate-500/10 border-slate-200 dark:border-slate-500/30 text-slate-700 dark:text-slate-400';
    }
  };

  const getInsightAccentColor = (type: AIInsight['type']) => {
    switch (type) {
      case 'critical': return 'text-red-600 dark:text-red-400';
      case 'warning': return 'text-amber-600 dark:text-amber-400';
      case 'suggestion': return 'text-blue-600 dark:text-blue-400';
      case 'info': return 'text-slate-600 dark:text-slate-400';
      default: return 'text-slate-600 dark:text-slate-400';
    }
  };

  const handleViewRequest = (entry: HAREntry) => {
    selectEntry(entry);
    setSidebarOpen(true);
  };

  if (!har) return null;

  return (
    <div id="ai-insights-section" className="card p-4 h-[450px] flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <OfflineIndicator 
          isCached={aiInsightsCached && !isOnline}
          onRetry={retryPendingAnalyses}
          pendingCount={pendingAnalysesCount}
        />
        {aiInsightsCached && isOnline && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg text-blue-700 dark:text-blue-400 text-xs">
            <Database className="w-3.5 h-3.5" />
            <span className="font-medium">Cached insights</span>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-1 -mx-1 custom-scrollbar">
        {aiInsightsLoading ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 py-8">
            <div className="relative">
              <div className="w-12 h-12 border-4 border-violet-200 dark:border-violet-900 rounded-full animate-pulse" />
              <Loader2 className="w-6 h-6 animate-spin text-violet-500 absolute top-3 left-3" />
            </div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 animate-pulse text-center max-w-[200px]">
              AI is currently analyzing your HAR file...
            </p>
          </div>
        ) : aiInsightsError ? (
          <div className="h-full flex flex-col items-center justify-center p-6 text-center">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-500/10 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">Analysis Interrupted</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-6 leading-relaxed">
              {aiInsightsError}
            </p>
            <button
              onClick={() => loadAIInsights()}
              className="w-full text-xs font-bold py-2.5 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-500/20 transition-all border border-red-200 dark:border-red-500/30"
            >
              Restart Analysis
            </button>
          </div>
        ) : !aiInsights || aiInsights.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-8 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-3xl group hover:border-violet-300 dark:hover:border-violet-500/30 transition-all duration-300">
            <div className="w-16 h-16 bg-violet-100 dark:bg-violet-500/10 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300 shadow-sm">
              <Sparkles className="w-8 h-8 text-violet-600 dark:text-violet-400" />
            </div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">Beta AI Analysis</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-6 leading-relaxed max-w-[220px]">
              Get AI-powered suggestions to help identify potential performance and security improvements.
            </p>
            <button
              onClick={() => loadAIInsights()}
              className="w-full py-3 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold rounded-2xl shadow-xl shadow-violet-500/20 active:scale-95 transition-all"
            >
              Analyze HAR File
            </button>
          </div>
        ) : (
          <div className={cn(
            "space-y-4 pb-4 relative",
            hasNewInsights && "animate-in fade-in slide-in-from-top-4 duration-500"
          )}>
            {hasNewInsights && (
              <div className="absolute -top-2 right-0 px-2 py-0.5 bg-violet-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-full animate-pulse">
                New
              </div>
            )}
            {aiInsights.map((insight, index) => {
              const matchedRequests = har?.log?.entries 
                ? findMatchingRequests(insight, har.log.entries)
                : [];
              const hasMatches = matchedRequests.length > 0;
              const docUrl = getDocumentationUrl(insight.category);
              
              return (
                <div
                  key={index}
                  className={cn(
                    'p-4 rounded-2xl border transition-all hover:shadow-lg hover:-translate-y-0.5 group',
                    getInsightStyles(insight.type)
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <h4 className="font-bold text-xs uppercase tracking-tight leading-none">{insight.title}</h4>
                      <span className="px-2 py-0.5 text-[9px] font-black bg-white/60 dark:bg-black/30 rounded-full uppercase tracking-widest opacity-80">
                        {insight.category}
                      </span>
                    </div>
                    <p className="text-xs opacity-90 leading-relaxed mb-3 font-medium">
                      {insight.description}
                    </p>

                    {insight.recommendation && (
                      <div className="p-3 bg-white/40 dark:bg-black/10 rounded-xl text-[11px] flex gap-3 border border-white/20 dark:border-white/5 mb-3">
                        <div className="mt-0.5">
                          <Zap className="w-3 h-3 text-violet-500" />
                        </div>
                        <span className="leading-normal font-medium">{insight.recommendation}</span>
                      </div>
                    )}

                    {hasMatches && (
                      <MatchedRequestsTable 
                        requests={matchedRequests}
                        onRequestClick={handleViewRequest}
                      />
                    )}

                    {docUrl && (
                      <a
                        href={docUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          "mt-3 inline-flex items-center gap-1.5 text-[10px] font-bold hover:underline transition-colors",
                          getInsightAccentColor(insight.type)
                        )}
                      >
                        <span>Learn more</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// AI Button for header - triggers analysis or shows already loaded
export function AIInsightsButton() {
  const { har, aiInsights, aiInsightsLoading, loadAIInsights } = useHARStore();

  if (!har) return null;

  const handleClick = () => {
    if (!aiInsights || aiInsights.length === 0) {
      // If no insights loaded yet, trigger the analysis
      loadAIInsights();
    }
    // Scroll to the AI insights section
    const aiInsightsSection = document.getElementById('ai-insights-section');
    if (aiInsightsSection) {
      aiInsightsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={aiInsightsLoading}
      className={cn(
        "btn-ghost p-2 relative group",
        aiInsights && aiInsights.length > 0 && "text-violet-500 bg-violet-50 dark:bg-violet-500/10",
        aiInsightsLoading && "opacity-50 cursor-not-allowed"
      )}
      title={aiInsights && aiInsights.length > 0 ? "AI analysis complete" : "Analyze HAR file with AI"}
    >
      <Sparkles className={cn(
        "w-5 h-5 transition-transform group-hover:scale-110 group-active:scale-95",
        aiInsightsLoading && "animate-pulse"
      )} />
      {aiInsights && aiInsights.length > 0 && !aiInsightsLoading && (
        <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-violet-600 rounded-full ring-2 ring-white dark:ring-slate-900 animate-pulse" />
      )}
    </button>
  );
}
